import React from 'react';
import { Modal, Row, Col, Typography, Card, Spin, Space, Button } from 'antd';
import { formatCurrency, formatDate } from 'utils/constants/functions';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface DebtDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ruleData: any;
  loading?: boolean;
  onRefresh?: () => void;
}

const DebtDetailModal: React.FC<DebtDetailModalProps> = ({
  isOpen,
  onClose,
  ruleData,
  loading = false,
  onRefresh,
}) => {
  // Debug log

  // Extract data from the correct path - API getSumPaymentCustomerDebit
  const debtItems = ruleData?.getSumPaymentCustomerDebit?.items || [];

  // Get the debt item (only one item with one type)
  const debtItem = debtItems[0] || {};
  const debtType = debtItem?.type;

  const rawTraditionalLimit = debtItem?.amountWagesTraditional || 0;
  const rawPremiumLimit = debtItem?.amountHighendLimit || 0;
  const rawPremiumTraditionalLimit =
    debtItem?.amountHighendTraditionalLimit || 0;
  const rawTraditionalDebt = debtItem?.customerDebitAmountLog ?? 0;
  const rawPremiumDebt = debtItem?.customerDebitAmountLog ?? 0;
  const rawPremiumTraditionalDebt = debtItem?.customerDebitAmountLog ?? 0;

  const limitQ10 = debtItem?.goldThreadQ10Traditional || 0;
  const limitQ10Date = debtItem?.dateQ10Traditional || '-';

  const formatDebtDate = (targetType?: string) => {
    if (targetType && debtType !== targetType) return '-';
    return debtItem?.dateDebit
      ? dayjs(debtItem.dateDebit).format('DD/MM/YYYY')
      : '-';
  };

  return (
    <Modal
      title={null}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
      closable={true}
    >
      <Spin spinning={loading}>
        {/* Header */}
        <div style={{ marginBottom: '24px', paddingRight: '40px' }}>
          <Row justify="space-between" align="middle">
            <Col flex="auto">
              <Title level={3} style={{ margin: 0 }}>
                Chi tiết công nợ
              </Title>
              <Text type="secondary">
                Cập nhật lần cuối:{' '}
                {debtItem?.dateDebit
                  ? dayjs(debtItem.dateDebit).format('DD/MM/YYYY HH:mm')
                  : '-'}
              </Text>
            </Col>
            <Col flex="none">
              <Button type="primary" onClick={onRefresh} loading={loading}>
                Cập nhật công nợ
              </Button>
            </Col>
          </Row>
        </div>

        {/* Summary Boxes */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={12}>
            <Card size="small" style={{ backgroundColor: '#f8f9fa' }}>
              <Title level={5} style={{ margin: '0 0 8px 0' }}>
                Hạn mức / Lần xuất bán
              </Title>
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Text strong>Q10:</Text>{' '}
                  {(debtItem?.goldThreadSalesLimit || 0).toFixed(4)}
                </Col>
                <Col span={12}>
                  <Text strong>VND:</Text>{' '}
                  {formatCurrency({
                    value: debtItem?.amountSalesLimit || 0,
                    isVND: true,
                  })}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" style={{ backgroundColor: '#f8f9fa' }}>
              <Title level={5} style={{ margin: '0 0 8px 0' }}>
                PNJP
              </Title>
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Text strong>Giữ hộ Q10:</Text>{' '}
                  {(debtItem?.pnjpDebitQ10Log || 0).toFixed(4)}
                </Col>
                <Col span={12}>
                  <Text strong>Nợ VND:</Text>{' '}
                  {formatCurrency({
                    value: debtItem?.pnjpDebitAmountLog || 0,
                    isVND: true,
                  })}
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Main Content with Gray Border */}
        <div
          style={{
            border: '2px solid #d9d9d9',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fafafa',
          }}
        >
          {/* Traditional Goods Section */}
          <Typography.Title
            level={5}
            style={{ margin: '0 0 16px 0', color: '#000' }}
          >
            Hàng truyền thống
          </Typography.Title>

          {/* Đường gạch ngang */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#d9d9d9',
              marginBottom: '8px',
            }}
          ></div>

          {/* Hạn mức nợ Q10 */}
          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ marginBottom: '8px', display: 'block' }}>
              Hạn mức nợ Q10
            </Text>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Text style={{ width: '100px' }}>Q10:</Text>
                  <Text strong>{Number(limitQ10 || 0).toFixed(4)}</Text>
                </div>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                  }}
                >
                  <Text style={{ width: '100px' }}>Ngày:</Text>
                  <Text strong>{limitQ10Date}</Text>
                </div>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Text style={{ width: '100px' }}>Công nợ:</Text>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    {debtType === 'DEB10_01'
                      ? Number(debtItem?.customerDebitQ10Log || 0).toFixed(4)
                      : '_'}
                  </Text>
                </div>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                  }}
                >
                  <Text style={{ width: '100px' }}>Ngày:</Text>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    {formatDebtDate('DEB10_01')}
                  </Text>
                </div>
              </Col>
            </Row>
          </div>

          {/* Hạn mức nợ Tiền công */}
          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ marginBottom: '8px', display: 'block' }}>
              Hạn mức nợ Tiền công
            </Text>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Text style={{ width: '100px' }}>VND:</Text>
                  <Text strong>
                    {formatCurrency({
                      value: rawTraditionalLimit,
                      isVND: true,
                    })}
                  </Text>
                </div>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                  }}
                >
                  <Text>Ngày:</Text>
                  <Text strong>{debtItem?.dateWagesTraditional || 0}</Text>
                </div>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Text style={{ width: '100px' }}>Công nợ:</Text>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    {debtType === 'DEB10_01'
                      ? formatCurrency({
                          value: rawTraditionalDebt,
                          isVND: true,
                        })
                      : '_'}
                  </Text>
                </div>
                <div
                  style={{
                    display: 'flex',

                    padding: '8px 0',
                  }}
                >
                  <Text style={{ width: '100px' }}>Ngày:</Text>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    {formatDebtDate('DEB10_01')}
                  </Text>
                </div>
              </Col>
            </Row>
          </div>

          {/* High-End Goods Section */}
          <Typography.Title
            level={5}
            style={{ margin: '24px 0 16px 0', color: '#000' }}
          >
            Hàng Cao Cấp
          </Typography.Title>

          {/* Đường gạch ngang */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#d9d9d9',
              marginBottom: '8px',
            }}
          ></div>

          {/* Title */}
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Hạn mức nợ Tiền</Text>
          </div>

          {/* 2 Columns Layout */}
          <div style={{ display: 'flex', gap: '24px' }}>
            {/* Column 1 - Hạn mức VND */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <Text style={{ width: '100px' }}>VND:</Text>
                <Text strong>
                  {formatCurrency({
                    value: rawPremiumLimit,
                    isVND: true,
                  })}
                </Text>
              </div>
              <div style={{ display: 'flex' }}>
                <Text style={{ width: '100px' }}>Ngày:</Text>
                <Text strong>{debtItem?.dateHighendLimit || 0}</Text>
              </div>
            </div>

            {/* Column 2 - Hiện tại Công nợ */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <Text style={{ width: '100px' }}>Công nợ:</Text>
                <Text strong style={{ color: '#ff4d4f' }}>
                  {debtType === 'DEB10_02'
                    ? formatCurrency({
                        value: rawPremiumDebt,
                        isVND: true,
                      })
                    : '_'}
                </Text>
              </div>
              <div style={{ display: 'flex' }}>
                <Text style={{ width: '100px' }}>Ngày:</Text>
                <Text strong style={{ color: '#ff4d4f' }}>
                  {formatDebtDate('DEB10_02')}
                </Text>
              </div>
            </div>
          </div>

          {/* High-End + Traditional Section */}
          <Typography.Title
            level={5}
            style={{ margin: '24px 0 16px 0', color: '#000' }}
          >
            Hàng Cao Cấp - Tiền công Hàng truyền thống
          </Typography.Title>

          {/* Đường gạch ngang */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#d9d9d9',
              marginBottom: '8px',
            }}
          ></div>

          {/* Title */}
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Hạn mức nợ Tiền</Text>
          </div>

          {/* 2 Columns Layout */}
          <div style={{ display: 'flex', gap: '24px' }}>
            {/* Column 1 - Hạn mức VND */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <Text style={{ width: '100px' }}>VND:</Text>
                <Text strong>
                  {formatCurrency({
                    value: rawPremiumTraditionalLimit,
                    isVND: true,
                  })}
                </Text>
              </div>
              <div style={{ display: 'flex' }}>
                <Text style={{ width: '100px' }}>Ngày:</Text>
                <Text strong>{debtItem?.dateHighendTraditionalLimit || 0}</Text>
              </div>
            </div>

            {/* Column 2 - Hiện tại Công nợ */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <Text style={{ width: '100px' }}>Công nợ:</Text>
                <Text strong style={{ color: '#ff4d4f' }}>
                  {debtType === 'DEB10_03'
                    ? formatCurrency({
                        value: rawPremiumTraditionalDebt,
                        isVND: true,
                      })
                    : '_'}
                </Text>
              </div>
              <div style={{ display: 'flex' }}>
                <Text style={{ width: '100px' }}>Ngày:</Text>
                <Text strong style={{ color: '#ff4d4f' }}>
                  {formatDebtDate('DEB10_03')}
                </Text>
              </div>
            </div>
          </div>

          {/* Effective Time Footer */}
          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Text type="secondary">
              Thời gian hiệu lực:{' '}
              {debtItem?.effectiveTimeFrom
                ? dayjs(debtItem.effectiveTimeFrom).format('DD/MM/YYYY')
                : '-'}{' '}
              -{' '}
              {debtItem?.effectiveTimeTo
                ? dayjs(debtItem.effectiveTimeTo).format('DD/MM/YYYY')
                : '-'}
            </Text>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default DebtDetailModal;
